// useProviderFormat.js

import { useContext } from 'react';
import { ArteFinalContext_v2 } from '../context/ArteFinalContext_v2';
import { ShoppingCartContext } from '../context/ShoppingCartContext';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { deleteOneFormatProvider } from '../api/carteleriaApi';
import toast from 'react-hot-toast';
import { getProviderFormatByFormatAndProvider } from '../api/providerFormatApi';

export const useProviderShoppingCart = () => {
  const { shoppingCart_v2, setShoppingCart_v2 } = useContext(ShoppingCartContext);
  const { arteFinal, formatSelected, formatProviderSelected, quantitySelected, setQuantitySelected, materialSelected } =
    useContext(ArteFinalContext_v2);

  const { formatoMaterial, ...restFormatSelected } = formatSelected;

  const handleAddProviderFormat = (setRequiredFormatChildren, setOpenModalSelectChildrenFormat) => {
    const minQuantity = arteFinal.minQuantity || 1;
    const quantityToAdd = quantitySelected >= minQuantity ? quantitySelected : minQuantity;

    if (formatSelected.childrenFormat.format.length > 0) {
      setRequiredFormatChildren(formatSelected.childrenFormat.required);

      const format = [];

      const { nameArteFinal, codeArteFinal, ...rest } = arteFinal;

      format.push({
        ...formatSelected,
        providerFormat: formatProviderSelected,
        price: formatProviderSelected.price,
        arteFinal: {
          ...rest,
          code: codeArteFinal,
          name: nameArteFinal,
          minQuantity,
        },
        checked: true,
        quantity: quantityToAdd,
        minQuantity,
      });

      formatSelected.childrenFormat.format.forEach((childrenFormat) => {
        format.push({
          ...childrenFormat,
          checked: true,
          originalQuantity: childrenFormat.quantity.quantity,
          minQuantity: childrenFormat.arteFinal.minQuantity || 1,
          quantity: !formatSelected.childrenFormat.required
            ? childrenFormat.arteFinal.minQuantity || 1
            : childrenFormat.quantity.quantity * quantityToAdd,
        });
      });

      setOpenModalSelectChildrenFormat(format);
      return;
    }

    if ((formatProviderSelected || formatSelected.formatoCustom) && quantityToAdd > 0) {
      const newShoppingCart = { ...shoppingCart_v2 };

      if (!newShoppingCart[arteFinal._id]) {
        newShoppingCart[arteFinal._id] = {
          id: arteFinal._id,
          name: arteFinal.nameArteFinal,
          code: arteFinal.codeArteFinal,
          imglink: arteFinal.imglink,
          montaje: arteFinal.isMontaje,
          minQuantity,
          formatos: {},
        };
      }

      newShoppingCart[arteFinal._id].formatos = {
        ...newShoppingCart[arteFinal._id].formatos,
        [formatSelected.formatoId]: {
          ...restFormatSelected,
          formatoMaterial: materialSelected ? materialSelected.nombre : formatSelected.formatoMaterial,
          material: materialSelected,
          quantity: quantityToAdd,
          providerFormat: formatProviderSelected.format === formatSelected.formatoId ? formatProviderSelected : null,
          price: formatProviderSelected.format === formatSelected.formatoId ? formatProviderSelected.price : null,
        },
      };

      setShoppingCart_v2(newShoppingCart);
    }
  };

  const handleDecrement = () => {
    const minQuantity = arteFinal.minQuantity || 1;
    const aaff = shoppingCart_v2[arteFinal._id];
    const formato = aaff?.formatos?.[formatSelected.formatoId];

    if (formato) {
      if (quantitySelected <= minQuantity) {
        // Si la cantidad es igual o menor que la mínima, eliminar el artículo del carrito
        const newShoppingCart = { ...shoppingCart_v2 };
        delete newShoppingCart[arteFinal._id].formatos[formatSelected.formatoId];
        if (Object.keys(newShoppingCart[arteFinal._id].formatos).length === 0) {
          delete newShoppingCart[arteFinal._id];
        }
        setShoppingCart_v2(newShoppingCart);
        setQuantitySelected(0);
      } else if (quantitySelected > minQuantity) {
        // Si la cantidad es mayor a la mínima, reducir en 1
        const newShoppingCart = { ...shoppingCart_v2 };
        newShoppingCart[arteFinal._id].formatos[formatSelected.formatoId].quantity -= 1;
        setShoppingCart_v2(newShoppingCart);
        setQuantitySelected(quantitySelected - 1);
      }
    } else if (quantitySelected > 0) {
      setQuantitySelected(quantitySelected - 1);
    }
  };

  const handleIncrement = () => {
    setQuantitySelected(quantitySelected + 1);

    const newShoppingCart = { ...shoppingCart_v2 };

    if (newShoppingCart[arteFinal._id]?.formatos[formatSelected.formatoId]) {
      newShoppingCart[arteFinal._id].formatos[formatSelected.formatoId].quantity += 1;
      setShoppingCart_v2(newShoppingCart);
    }
  };

  const updateCartManually = () => {
    const minQuantity = arteFinal.minQuantity || 1;
    const quantityToUpdate = quantitySelected >= minQuantity ? quantitySelected : 0;

    const newShoppingCart = { ...shoppingCart_v2 };
    if (quantitySelected === 0) {
      handleDecrement();
    } else if (quantitySelected > 0) {
      if (newShoppingCart[arteFinal._id]?.formatos[formatSelected.formatoId]) {
        if (quantitySelected < minQuantity) {
          setQuantitySelected(minQuantity);
          newShoppingCart[arteFinal._id].formatos[formatSelected.formatoId].quantity = minQuantity;
        } else {
          newShoppingCart[arteFinal._id].formatos[formatSelected.formatoId].quantity = quantitySelected;
        }
        setShoppingCart_v2(newShoppingCart);
      }
    }
  };

  return { handleAddProviderFormat, handleDecrement, handleIncrement, updateCartManually };
};

// Función para eliminar un proveedor específico
export const useDeleteProviderFormat = (idFormatProvider, idArte) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      let token = await getAccessTokenSilently();
      await deleteOneFormatProvider(token, idFormatProvider);
      toast.success('Formato eliminado');
    },
    onError: (error) => {
      toast.error(error.message, {
        id: 'delete-provider-format-not-found',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['formats-arte-final', idArte]);
    },
  });
  return { mutation };
};

// Función para obtener el formato y proveedor específico
export const useProviderFormatByFormatAndProvider = (formatId, providerId) => {
  return useQuery({
    queryKey: ['providerFormat', formatId, providerId],
    queryFn: () => getProviderFormatByFormatAndProvider(formatId, providerId),
    suspense: true,
    enabled: !!formatId && !!providerId,
  });
};
